import { gql } from "@apollo/client";

export const LOCATIONS_QUERY = gql`query Locations {
    locationsTree
}
`;

// Optimized location map queries — lightweight tree + detail on demand

export const LOCATION_MAP_TREE_QUERY = gql`
  fragment LocationMapTreeFields on LocationMapNode {
    id
    locationName
    locationType
    locationTypeName
    parentId
    floorIndex
    floorType
  }
  query LocationMapTree {
    locationMapTree {
      ...LocationMapTreeFields
      address
      children {
        ...LocationMapTreeFields
        children {
          ...LocationMapTreeFields
          children {
            ...LocationMapTreeFields
            children {
              ...LocationMapTreeFields
              children {
                ...LocationMapTreeFields
                children {
                  ...LocationMapTreeFields
                  children {
                    ...LocationMapTreeFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const LOCATION_MAP_DETAIL_QUERY = gql`
  query LocationMapDetail($locationId: ID!, $refreshFacilityGraph: Boolean, $includeAssets: Boolean) {
    locationMapDetail(locationId: $locationId, refreshFacilityGraph: $refreshFacilityGraph, includeAssets: $includeAssets) {
      id
      locationName
      locationType
      locationTypeName
      address
      city
      state
      country
      zipcode
      description
      coordinates
      parentId
      metadata
      dynamic
      standards
    }
  }
`;

/**
 * Shared lean asset payload for location map panel + FloorPlan2D canvas.
 * Single operation so Apollo dedupes network calls when both views need the same location.
 */

export const FLOOR_PLAN_2D_ASSETS_QUERY = gql`
  query FloorPlan2DAssets($locationId: ID!, $includeSubLocations: Boolean) {
    getAssetsByLocationId(locationId: $locationId, includeSubLocations: $includeSubLocations) {
      total
      assets {
        id
        name
        description
        status
        locationIds
        position
        obligations
        metadata
        assetRelations {
          id
          assetId
          fedFromId
        }
        product {
          type {
            id
            name
            iconName
            iconColor
            iconType
          }
          category {
            id
            name
            iconColor
          }
        }
        responsibleUsers {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

/** Breaker detail for a single panel asset — fetched on demand from the floor plan side panel. */
export const FLOOR_PLAN_PANEL_BREAKER_QUERY = gql`
  query FloorPlanPanelBreaker($id: ID!) {
    asset(id: $id) {
      id
      breakerManagement {
        panelConfig {
          layoutMode
          numberOfBreakers
          breakerStartNumber
          mainBreaker {
            enabled
            position
            poles
            breakerName
            amps
            phase
            breakerType
          }
        }
        breakers {
          relationId
          assetIds
          breakerName
          poles
          amps
          phase
          breakerType
          description
          slot
          slotSpan
          columnIndex
          displayOrder
          affectedAreas {
            id
            locationName
            locationAddress
          }
        }
      }
    }
  }
`;

/** @deprecated Use FLOOR_PLAN_2D_ASSETS_QUERY — same document for Apollo cache sharing. */
export const GET_ASSETS_BY_LOCATION_MAP_LITE_QUERY = FLOOR_PLAN_2D_ASSETS_QUERY;

// Assets Query
// Get Company Products Query (with assets)
// Assets Count Query - Only returns count, no asset data

export const GET_ASSETS_BY_LOCATION_ID_QUERY = gql`
  query GetAssetsByLocationId($locationId: ID!, $includeSubLocations: Boolean) {
    getAssetsByLocationId(
      locationId: $locationId
      includeSubLocations: $includeSubLocations
    ) {
      total
      assets {
        id
        name
        description
        productId
        installationDate
        maintenanceIds
        userIds
        locationIds
        position
        serialNumber
        status
        coverage
        locationAddress
        maintenance
        upcomingInspection
        responsibleUsers {
          id
          firstName
          lastName
          email
        }
        locations {
          locationName
        }
        product {
          id
          name
          category {
            name
            iconColor
          }
          type {
            name
          }
        }
        files {
          id
          file_name
          file_url
          mime_type
          belongs_to_type
          belongs_to_id
        }
        assetRelations {
          id
          assetId
          fedFromId
        }
      }
    }
  }
`;

// Plans Query

export const LOCATION_TIMELINE_QUERY = gql`
  query LocationTimeline($locationId: ID!) {
    locationTimeline(locationId: $locationId) {
      id
      locationId
      type
      title
      notes
      evidenceFileIds
      metadata
      createdBy
      createdAt
      updatedAt
      files {
        id
        file_name
        file_url
        mime_type
        file_size
        upload_status
        created_at
      }
      createdByUser {
        id
        firstName
        lastName
        email
        role
      }
    }
  }
`;

export const LOCATION_DELETE_IMPACT_QUERY = gql`
  query LocationDeleteImpact($id: ID!) {
    locationDeleteImpact(id: $id) {
      locationName
      subLocationCount
      assetsToDeleteCount
      assetsToUnlinkCount
      workOrderCount
      documentCount
      obligationCount
      inspectionOrderCount
    }
  }
`;
